'use client';

import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import BlurredDecorations from "@/components/blurred-decorations/blurred-decorations";
import AddStoryCard from "@/components/card/add-story-card/add-story-card";
import RecordCtaCard from "@/components/card/record-cta-card/record-cta-card";
import StoryCard from "@/components/card/story-card/story-card";
import { STORIES_BASE_PATH } from "@/constants/routes";
import { useApp } from '@/context/AppContext';


const BATCH_SIZE = 12;
const RECORD_CTA_MARKER = { isRecordCtaCard: true };
const ADD_STORY_MARKER = { isAddStoryCard: true };

const floorToMultiple = (number, multipleOf) => {
  return Math.floor(number / multipleOf) * multipleOf;
}

const splitIntoColumns = (items, numColumns) => {
  const columns = Array.from({ length: numColumns }, () => []);
  items.forEach((item, index) => {
    item.__index = index;

    // NOTE controls placement of last image in case it needs to be tweaked in the future
    // THIS WILL DEPEND ON THE FINAL LOOK OF THE UPLOADED IMAGES
    if ((index === items.length - 1)) {
      if (columns[2]) {
        columns[2].push(item);
      } else if (columns[1]) {
        columns[1].push(item);
      }
    } else {
      columns[index % numColumns].push(item);
    }
  });
  return columns;
};

export default function StoriesGallery({ items }) {
  const {
    isMobile,
    currentTransitionName,
    getSavedScrollPosition,
  } = useApp();
  const [maxBatchIndex, setMaxBatchIndex] = useState(0);
  const gridRef = useRef(null);
  const colsRef = useRef([]);
  const hasRestoredScroll = useRef(false);

  // Restore scroll position when returning from story overlay
  useEffect(() => {
    if (currentTransitionName === "overlay-out" && !hasRestoredScroll.current) {
      const savedPosition = getSavedScrollPosition();
      if (savedPosition !== null) {
        hasRestoredScroll.current = true;
        window.scrollTo(0, savedPosition);
      }
    }
    // Reset flag when transition changes away from overlay-out
    if (currentTransitionName !== "overlay-out") {
      hasRestoredScroll.current = false;
    }
  }, [currentTransitionName, getSavedScrollPosition]);

  const onBatchEnter = useCallback((batchIndex) => {
    setMaxBatchIndex((prev) => Math.max(prev, batchIndex));
  }, []);

  // Intercept Tab within the grid to follow logical (left-to-right) order
  // instead of DOM (column-by-column) order
  const handleGridKeyDown = useCallback((e) => {
    if (e.key !== "Tab") return;

    const grid = gridRef.current;
    if (!grid) return;

    const cards = Array.from(grid.querySelectorAll("[data-focus-index]")).sort(
      (a, b) => Number(a.dataset.focusIndex) - Number(b.dataset.focusIndex),
    );

    const active = document.activeElement?.closest("[data-focus-index]");
    const currentIdx = cards.indexOf(active);
    if (currentIdx === -1) return;

    const nextIdx = e.shiftKey ? currentIdx - 1 : currentIdx + 1;

    e.preventDefault();

    if (nextIdx < 0 || nextIdx >= cards.length) {
      const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_ELEMENT, {
        acceptNode(node) {
          if (grid.contains(node)) return NodeFilter.FILTER_REJECT;
          if (node.matches('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')) return NodeFilter.FILTER_ACCEPT;
          return NodeFilter.FILTER_SKIP;
        },
      });
      walker.currentNode = grid;
      const target = nextIdx >= cards.length ? walker.nextNode() : walker.previousNode();
      target?.focus();
      return;
    }

    const focusable = cards[nextIdx].querySelector(
      'a[href], button, [tabindex="0"]',
    );
    focusable?.focus();
  }, []);
  
  const columns = useMemo(() => {
    let recordCtaIndex, addStoryIndex;

    // console.log(items.length);

    recordCtaIndex = items.length < 80 ? 
      isMobile ? 2 : Math.max(floorToMultiple(0.25 * items.length, 4), 4)
      : isMobile ? 8 : 48
    
    
    addStoryIndex = items.length < 80 ? 
      isMobile ? 4 : Math.max(floorToMultiple(0.5 * items.length, 4) + 1, 13)
      : isMobile ? 12 : 57

    // Insert markers in order (lower index first, accounting for shifts)
    const result = [...items];

    const sorted = [
      { index: recordCtaIndex, marker: RECORD_CTA_MARKER },
      { index: addStoryIndex, marker: ADD_STORY_MARKER },
    ].sort((a, b) => a.index - b.index);

    sorted.forEach(({ index, marker }, i) => {
      result.splice(index + i, 0, marker);
    });

    return splitIntoColumns(result, isMobile ? 2 : 4);
  }, [items, isMobile]);

  const addStoryCardLink = {
    href: `${STORIES_BASE_PATH}/250-stories/upload`,
  };

  return (
    <div ref={gridRef} className="mx-auto grid-4 pt-1 relative overflow-x-clip mt-16 lg:mt-19 pb-10" onKeyDown={handleGridKeyDown}>
      <BlurredDecorations
        count={Math.ceil(items.length / 4)}
        isFullPage={true}
        categoryId="stories"
      />
      {columns.map((column, colIndex) => (
        <div
          key={colIndex}
          ref={(el) => (colsRef.current[colIndex] = el)}
          className={`col-span-1 flex flex-col gap-y-5 will-change-transform lg:gap-y-10 ${colIndex === 1 || colIndex === 3 ? "mt-12.5 lg:mt-22" : ""}`}
        >
          {column.map((item, itemIndex) => {
            const batchIndex = Math.floor(item.__index / BATCH_SIZE);
            const cardIndexWithinBatch = item.__index % BATCH_SIZE;
            const isFirstInBatch = cardIndexWithinBatch === 0;
            const isParentInView = batchIndex <= maxBatchIndex;
            const isHidden = batchIndex > maxBatchIndex + 1;

            if (item.isRecordCtaCard) {
              return (
                <div key="record-cta-card" data-focus-index={item.__index}>
                  <RecordCtaCard
                    key="record-cta-card"
                    isParentInView={isParentInView}
                    index={cardIndexWithinBatch}
                    isFirstInBatch={isFirstInBatch}
                    batchIndex={batchIndex}
                    onBatchEnter={onBatchEnter}
                    isHidden={isHidden}
                    isSmall={true}
                  />
                </div>
              );
            }

            if (item.isAddStoryCard) {
              return (
                <div key="add-story-card" data-focus-index={item.__index}>
                  <AddStoryCard
                    key="add-story-card"
                    hasBackground={true}
                    isParentInView={isParentInView}
                    index={cardIndexWithinBatch}
                    isFirstInBatch={isFirstInBatch}
                    batchIndex={batchIndex}
                    onBatchEnter={onBatchEnter}
                    isHidden={isHidden}
                    text="Add your story to the gallery"
                    link={addStoryCardLink}
                  />
                </div>
              );
            }

            return (
              <div key={itemIndex + '' + item.slug} data-focus-index={item.__index}>
                <StoryCard
                  key={itemIndex + "" + item.slug}
                  item={item}
                  isParentInView={isParentInView}
                  cardIndexWithinBatch={cardIndexWithinBatch}
                  isFirstInBatch={isFirstInBatch}
                  batchIndex={batchIndex}
                  onBatchEnter={onBatchEnter}
                  isHidden={isHidden}
                />
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
